import React, { useState, useEffect } from 'react';
import {
  Button,
  Container,
  TextField,
  Table,
  Modal,
  TableBody,
  TableCell,
  Box,
  Typography,
  Paper,
  InputAdornment,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  useTheme,
  styled,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  Visibility as ViewIcon,
  KeyboardDoubleArrowLeft as ChevronLeft,
  KeyboardDoubleArrowRight as ChevronRight,
  Add as AddIcon,
} from '@mui/icons-material';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { Navigate, useNavigate } from 'react-router-dom';

const CustomTableScrollbar = styled('div')(({ theme }) => ({
  '&::-webkit-scrollbar': {
    height: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} transparent`,
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Question = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUserGuideModal, setShowUserGuideModal] = useState(false);
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
  const [showEditQuestionModal, setShowEditQuestionModal] = useState(false);
  const [showViewQuestionModal, setShowViewQuestionModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalType, setSuccessModalType] = useState('add');
  const [selectedQuestion, setSelectedQuestion] = useState({ questionId: null, questionText: '' });
  const [importFile, setImportFile] = useState(null);
  const [formData, setFormData] = useState({
    courseName: '',
    moduleName: '',
    topicName: '',
    questionType: '',
    question: '',
    isActive: false,
  });

  // Static data for questions and dropdowns
  const staticQuestionData = [
    {
      questionId: 1,
      questionText: 'What is the capital of France?',
      course: 'Geography',
      module: 'World Capitals',
      topic: 'Europe',
      type: 'Multiple Choice',
      isActive: true,
    },
    {
      questionId: 2,
      questionText: 'Explain the process of photosynthesis.',
      course: 'Biology',
      module: 'Plant Physiology',
      topic: 'Photosynthesis',
      type: 'Descriptive',
      isActive: true,
    },
    {
      questionId: 3,
      questionText: 'What is the formula for water?',
      course: 'Chemistry',
      module: 'Basic Chemistry',
      topic: 'Chemical Formulas',
      type: 'Short Answer',
      isActive: true,
    },
    {
      questionId: 4,
      questionText: 'Who wrote "Romeo and Juliet"?',
      course: 'Literature',
      module: 'Shakespeare',
      topic: 'Plays',
      type: 'Multiple Choice',
      isActive: true,
    },
    {
      questionId: 5,
      questionText: 'Solve for x: 2x + 3 = 7',
      course: 'Mathematics',
      module: 'Algebra',
      topic: 'Linear Equations',
      type: 'Problem Solving',
      isActive: true,
    },
  ];

  const staticCourseData = [
    { id: 1, name: 'Computer Science' },
    { id: 2, name: 'Mathematics' },
    { id: 3, name: 'Physics' },
    { id: 4, name: 'Chemistry' },
    { id: 5, name: 'Biology' },
    { id: 6, name: 'Geography' },
    { id: 7, name: 'Literature' },
  ];

  const staticModuleData = [
    { id: 1, name: 'Introduction' },
    { id: 2, name: 'Advanced' },
    { id: 3, name: 'Practical' },
    { id: 4, name: 'World Capitals' },
    { id: 5, name: 'Plant Physiology' },
    { id: 6, name: 'Basic Chemistry' },
    { id: 7, name: 'Shakespeare' },
    { id: 8, name: 'Algebra' },
  ];

  const staticTopicData = [
    { id: 1, name: 'Basics' },
    { id: 2, name: 'Intermediate' },
    { id: 3, name: 'Complex' },
    { id: 4, name: 'Europe' },
    { id: 5, name: 'Photosynthesis' },
    { id: 6, name: 'Chemical Formulas' },
    { id: 7, name: 'Plays' },
    { id: 8, name: 'Linear Equations' },
  ];

  const staticQuestionTypeData = [
    { id: 1, name: 'Multiple Choice' },
    { id: 2, name: 'Descriptive' },
    { id: 3, name: 'Short Answer' },
    { id: 4, name: 'Problem Solving' },
  ];

  const [questionData, setQuestionData] = useState(staticQuestionData);
  const [filteredData, setFilteredData] = useState(staticQuestionData);

  const handleAddQuestion = () => {
    setFormData({
      courseName: '',
      moduleName: '',
      topicName: '',
      questionType: '',
      question: '',
      isActive: false,
    });
    setShowAddQuestionModal(true);
  };

  const handleEdit = (question) => {
    setSelectedQuestion({ questionId: question.questionId, questionText: question.questionText });
    setFormData({
      courseName: question.course,
      moduleName: question.module,
      topicName: question.topic,
      questionType: question.type,
      question: question.questionText,
      isActive: question.isActive,
    });
    setShowEditQuestionModal(true);
  };

  const handleView = (question) => {
    setSelectedQuestion({ questionId: question.questionId, questionText: question.questionText });
    setFormData({
      courseName: question.course,
      moduleName: question.module,
      topicName: question.topic,
      questionType: question.type,
      question: question.questionText,
      isActive: question.isActive,
    });
    setShowViewQuestionModal(true);
  };

  const handleShowDeleteModal = (questionId, questionText) => {
    setSelectedQuestion({ questionId, questionText });
    setShowDeleteModal(true);
  };

  const handleDelete = () => {
    setQuestionData((prevQuestions) =>
      prevQuestions.filter((question) => question.questionId !== selectedQuestion.questionId)
    );
    setSnackbarMessage(`Question "${selectedQuestion.questionText}" deleted successfully!`);
    setSnackbarSeverity('success');
    setOpenSnackbar(true);
    setShowDeleteModal(false);
    // setShowSuccessModal(true);
    setSuccessModalType('delete');
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleFileChange = (e) => {
    setImportFile(e.target.files[0]);
  };

  const handleImport = () => {
    if (importFile) {
      setSnackbarMessage('File imported successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      setShowImportModal(false);
      setImportFile(null);
    } else {
      setSnackbarMessage('Please select a file to import');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSave = () => {
    if (
      formData.courseName &&
      formData.moduleName &&
      formData.topicName &&
      formData.questionType &&
      formData.question
    ) {
      const newQuestion = {
        questionId: questionData.length + 1,
        questionText: formData.question,
        course: formData.courseName,
        module: formData.moduleName,
        topic: formData.topicName,
        type: formData.questionType,
        isActive: formData.isActive,
      };
      setQuestionData([...questionData, newQuestion]);
      setShowAddQuestionModal(false);
      setShowSuccessModal(true);
      setSuccessModalType('add');
      setFormData({
        courseName: '',
        moduleName: '',
        topicName: '',
        questionType: '',
        question: '',
        isActive: false,
      });
      setSnackbarMessage('Question added successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    } else {
      setSnackbarMessage('Please fill all required fields');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  const handleUpdate = () => {
    if (
      formData.courseName &&
      formData.moduleName &&
      formData.topicName &&
      formData.questionType &&
      formData.question
    ) {
      const updatedQuestions = questionData.map((question) =>
        question.questionId === selectedQuestion.questionId
          ? {
            ...question,
            questionText: formData.question,
            course: formData.courseName,
            module: formData.moduleName,
            topic: formData.topicName,
            type: formData.questionType,
            isActive: formData.isActive,
          }
          : question
      );
      setQuestionData(updatedQuestions);
      setShowEditQuestionModal(false);
      setShowSuccessModal(true);
      setSuccessModalType('edit');
      setFormData({
        courseName: '',
        moduleName: '',
        topicName: '',
        questionType: '',
        question: '',
        isActive: false,
      });
      setSnackbarMessage('Question updated successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    } else {
      setSnackbarMessage('Please fill all required fields');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  const handleCancel = () => {
    setShowEditQuestionModal(false);
    setShowViewQuestionModal(false);
    setShowAddQuestionModal(false);
    setFormData({
      courseName: '',
      moduleName: '',
      topicName: '',
      questionType: '',
      question: '',
      isActive: false,
    });
  };

  const handleReset = () => {
    setFormData({
      courseName: '',
      moduleName: '',
      topicName: '',
      questionType: '',
      question: '',
      isActive: false,
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  useEffect(() => {
    setFilteredData(
      questionData.filter((question) =>
        Object.values(question).some((value) =>
          value.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    );
  }, [searchTerm, questionData]);

  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Container sx={{ maxWidth: '100%', boxSizing: 'border-box' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          p: 2,
          boxShadow: 'none',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <TextField
  variant="outlined"
  placeholder="Search..."
  value={searchTerm}
  onChange={(e) => setSearchTerm(e.target.value)}
  InputProps={{
    startAdornment: (
      <InputAdornment position="start">
        <SearchIcon
          sx={{
            color: theme.palette.primary.main,
            marginLeft: 1.5,
            fontSize: 24,
          }}
        />
      </InputAdornment>
    ),
    endAdornment: searchTerm && (
      <InputAdornment position="end">
        <IconButton
          size="small"
          onClick={() => setSearchTerm('')}
          sx={{
            color: "grey",
            marginRight: 1,
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </InputAdornment>
    ),
    sx: {
      borderRadius: '30px',
      backgroundColor: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(255, 255, 255, 1)',
      boxShadow: theme.palette.mode === 'dark'
        ? '0 2px 8px rgba(0, 0, 0, 0.3)'
        : '0 2px 8px rgba(0, 0, 0, 0.1)',
      '& .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
      },
      '&:hover': {
        backgroundColor: theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.2)'
          : 'rgba(240, 240, 240, 1)',
        boxShadow: theme.palette.mode === 'dark'
          ? '0 2px 12px rgba(0, 0, 0, 0.4)'
          : '0 2px 12px rgba(0, 0, 0, 0.15)',
      },
      '&.Mui-focused': {
        backgroundColor: theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.25)'
          : 'rgba(255, 255, 255, 1)',
        boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
        '& .MuiOutlinedInput-notchedOutline': {
          border: `2px solid ${theme.palette.primary.main}`,
        },
      },
      paddingLeft: 1.5,
      height: '50px',
      transition: 'all 0.3s ease',
    },
  }}
  sx={{
    width: 350,
    maxWidth: '100%',
    '& .MuiInputBase-input': {
      padding: '10px 14px',
      color: theme.palette.text.primary,
      fontWeight: 500,
    },
  }}
/>


          <Button
            variant="outlined"
            color="warning"
            onClick={() => setShowImportModal(true)}
            sx={{
              mr: 1,
              width: '100px',
              borderRadius: '10px',
              height: '50px',
              padding: '10px 0',
              fontWeight: 'bold',
              '&:hover': { borderColor: theme.palette.warning.dark, color: theme.palette.warning.dark },
            }}
            startIcon={<CloudUploadIcon />}
          >
            Import
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate("/add-question")}
            sx={{
              width: '160px',
              borderRadius: '10px',
              height: '50px',
              padding: '10px 0',
              fontWeight: 'bold',
              backgroundColor: theme.palette.primary.main,
              '&:hover': { backgroundColor: theme.palette.primary.dark },
            }}
            startIcon={<AddIcon />}
          >
            Add Question
          </Button>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <IconButton
            onClick={() => setShowUserGuideModal(true)}
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              minWidth: 0, // Remove MUI's default padding
            }}
          >
            <InfoOutlineIcon sx={{ fontSize: 30 }} />
          </IconButton>
        </Box>

      </Box>
      <Paper
        elevation={3}
        sx={{
          paddingBottom: 0,
          maxWidth: '100%',
          boxSizing: 'border-box',
          mx: 'auto',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '16px',
        }}
      >
        <CustomTableScrollbar sx={{ width: '100%', overflowX: 'auto' }}>
          <TableContainer sx={{ width: '100%', borderRadius: '16px' }}>
            <Table
              sx={{
                minWidth: '1300px',
                '& .MuiTableHead-root .MuiTableCell-root': {
                  fontWeight: 'bold',
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                  fontSize: '1.1rem',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>S.No</TableCell>
                  <TableCell>Question</TableCell>
                  <TableCell>Course</TableCell>
                  <TableCell>Module</TableCell>
                  <TableCell>Topic</TableCell>
                  <TableCell>Type of Question</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((question, index) => (
                    <TableRow
                      key={question.questionId}
                      sx={{
                        backgroundColor: index % 2 === 0 ? theme.palette.background.paper : theme.palette.background.paper,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <TableCell sx={{ fontSize: '1rem' }}>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                      <TableCell sx={{ fontSize: '1rem' }}>{question.questionText}</TableCell>
                      <TableCell sx={{ fontSize: '1rem' }}>{question.course}</TableCell>
                      <TableCell sx={{ fontSize: '1rem' }}>{question.module}</TableCell>
                      <TableCell sx={{ fontSize: '1rem' }}>{question.topic}</TableCell>
                      <TableCell sx={{ fontSize: '1rem' }}>{question.type}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          onClick={() => handleView(question)}
                          sx={{
                            color: theme.palette.primary.light,
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                          }}
                        >
                          <ViewIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => navigate(`/edit-question/${question.questionId}`)}
                          sx={{
                            color: theme.palette.warning.main,
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleShowDeleteModal(question.questionId, question.questionText)}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow sx={{
                    backgroundColor: theme.palette.background.paper,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}>
                    <TableCell colSpan={7} align="center" sx={{ fontSize: '1rem', padding: '20px', wordSpacing: 7 }}>
                      No Data Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CustomTableScrollbar>
      </Paper>

      {filteredData.length > 0 && (
        <Box
          sx={{
            maxWidth: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
            p: 2,
            mx: 'auto',
            pt: 2,
          }}
        >
          <Typography variant="body2" color={theme.palette.primary.main}>
            Page <strong>{page}</strong> of <strong>{Math.ceil(filteredData.length / rowsPerPage)}</strong>
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 1}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronLeft />
            </IconButton>
            {[...Array(Math.ceil(filteredData.length / rowsPerPage))].map((_, index) => (
              <IconButton
                key={index + 1}
                onClick={() => handleChangePage(null, index + 1)}
                sx={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: page === index + 1 ? theme.palette.primary.main : 'transparent',
                  color: page === index + 1 ? theme.palette.primary.contrastText : theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: page === index + 1 ? theme.palette.primary.dark : theme.palette.action.hover,
                  },
                }}
              >
                {index + 1}
              </IconButton>
            ))}
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage)}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronRight />
            </IconButton>
            <FormControl size="small" sx={{ minWidth: 100, ml: 1 }}>
              <InputLabel id="rows-per-page-label" sx={{ color: theme.palette.text.primary }}>
                Rows
              </InputLabel>
              <Select
                labelId="rows-per-page-label"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                label="Rows"
                sx={{
                  height: '36px',
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.text.primary,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                {rowsPerPageOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Delete Modal */}
      <Modal
        open={showDeleteModal}
        onClose={handleCloseDeleteModal}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
            maxHeight: '80vh',
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Delete Confirmation ⚠️
            </Typography>
            <IconButton onClick={handleCloseDeleteModal} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ maxHeight: 'calc(80vh - 64px)', overflowY: 'auto', padding: '20px' }}>
            <Typography sx={{ mb: 2 }}>
              Are you sure you want to delete the question "{selectedQuestion.questionText}"?
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleCloseDeleteModal}
                sx={{ mr: 1 }}
               data-app-cancel="true">
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
              >
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Import Modal */}
      <Modal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
            maxHeight: '80vh',
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Import Questions
            </Typography>
            <IconButton onClick={() => setShowImportModal(false)} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ maxHeight: 'calc(80vh - 64px)', overflowY: 'auto', padding: '20px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="contained"
                component="label"
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  '&:hover': { backgroundColor: theme.palette.primary.dark },
                }}
              >
                Select File
                <input type="file" hidden onChange={handleFileChange} />
              </Button>
              {importFile && (
                <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                  Selected: {importFile.name}
                </Typography>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleImport}
                  sx={{
                    mr: 2,
                    backgroundColor: theme.palette.primary.main,
                    '&:hover': { backgroundColor: theme.palette.primary.dark },
                  }}
                  disabled={!importFile}
                >
                  IMPORT
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => setShowImportModal(false)}
                >
                  CANCEL
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Success Modal */}
      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
            maxHeight: '80vh',
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
              {successModalType === 'add' ? 'Question Added ✅' : successModalType === 'edit' ? 'Question Updated ✅' : 'Question Deleted ✅'}
            </Typography>
            <IconButton onClick={() => setShowSuccessModal(false)} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ maxHeight: 'calc(80vh - 64px)', overflowY: 'auto', padding: '20px' }}>
            <Typography sx={{ mb: 2 }}>
              {successModalType === 'add' ? 'Question added successfully.' : successModalType === 'edit' ? 'Question updated successfully.' : 'Question deleted successfully.'}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                onClick={() => setShowSuccessModal(false)}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  '&:hover': { backgroundColor: theme.palette.primary.dark },
                }}
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Add Question Modal */}
      <Modal
        open={showAddQuestionModal}
        onClose={handleCancel}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <CustomTableScrollbar
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 700,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
            maxHeight: '90vh',
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Add Question
            </Typography>
            <IconButton onClick={handleCancel} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ maxHeight: 'calc(80vh - 64px)', overflowY: 'auto', padding: '20px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControlLabel
                control={<Checkbox name="isActive" checked={formData.isActive} onChange={handleChange} />}
                label="Is Active"
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl sx={{ width: 300, maxWidth: '100%' }} required>
                  <InputLabel id="courseName-label" sx={{ color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.primary.main } }}>
                    Course Name
                  </InputLabel>
                  <Select
                    labelId="courseName-label"
                    name="courseName"
                    value={formData.courseName}
                    label="Course Name"
                    onChange={handleChange}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.text.primary,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>Select a course</em>
                    </MenuItem>
                    {staticCourseData.map((course) => (
                      <MenuItem key={course.id} value={course.name}>
                        {course.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ width: 300, maxWidth: '100%' }} required>
                  <InputLabel id="moduleName-label" sx={{ color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.primary.main } }}>
                    Module Name
                  </InputLabel>
                  <Select
                    labelId="moduleName-label"
                    name="moduleName"
                    value={formData.moduleName}
                    label="Module Name"
                    onChange={handleChange}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.text.primary,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>Select a module</em>
                    </MenuItem>
                    {staticModuleData.map((module) => (
                      <MenuItem key={module.id} value={module.name}>
                        {module.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl sx={{ width: 300, maxWidth: '100%' }} required>
                  <InputLabel id="topicName-label" sx={{ color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.primary.main } }}>
                    Topic Name
                  </InputLabel>
                  <Select
                    labelId="topicName-label"
                    name="topicName"
                    value={formData.topicName}
                    label="Topic Name"
                    onChange={handleChange}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.text.primary,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>Select a topic</em>
                    </MenuItem>
                    {staticTopicData.map((topic) => (
                      <MenuItem key={topic.id} value={topic.name}>
                        {topic.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ width: 300, maxWidth: '100%' }} required>
                  <InputLabel id="questionType-label" sx={{ color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.primary.main } }}>
                    Question Type
                  </InputLabel>
                  <Select
                    labelId="questionType-label"
                    name="questionType"
                    value={formData.questionType}
                    label="Question Type"
                    onChange={handleChange}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.text.primary,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>Select a question type</em>
                    </MenuItem>
                    {staticQuestionTypeData.map((type) => (
                      <MenuItem key={type.id} value={type.name}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Question"
                  variant="outlined"
                  fullWidth
                  name="question"
                  value={formData.question}
                  onChange={handleChange}
                  required
                  multiline
                  rows={4}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.text.primary,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    },
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.primary,
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: theme.palette.primary.main,
                    },
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Tooltip
                  title={
                    !formData.courseName
                      ? 'Select Course'
                      : !formData.moduleName
                        ? 'Select Module'
                        : !formData.topicName
                          ? 'Select Topic'
                          : !formData.questionType
                            ? 'Select Question Type'
                            : !formData.question
                              ? 'Enter Question'
                              : ''
                  }
                  arrow
                >
                  <span>
                    <Button
                      variant="contained"
                      onClick={handleSave}
                      sx={{
                        mr: 2,
                        backgroundColor: theme.palette.primary.main,
                        '&:hover': { backgroundColor: theme.palette.primary.dark },
                      }}
                      disabled={
                        !formData.courseName ||
                        !formData.moduleName ||
                        !formData.topicName ||
                        !formData.questionType ||
                        !formData.question
                      }
                    >
                      SAVE
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleReset}
                  sx={{ mr: 2 }}
                >
                  RESET
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleCancel}
                >
                  CANCEL
                </Button>
              </Box>
            </Box>
          </Box>
        </CustomTableScrollbar>
      </Modal>

      {/* Edit Question Modal */}
      <Modal
        open={showEditQuestionModal}
        onClose={handleCancel}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <CustomTableScrollbar
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 700,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
            maxHeight: '90vh',
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Edit Question
            </Typography>
            <IconButton onClick={handleCancel} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ maxHeight: 'calc(80vh - 64px)', overflowY: 'auto', padding: '20px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControlLabel
                control={<Checkbox name="isActive" checked={formData.isActive} onChange={handleChange} />}
                label="Is Active"
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl sx={{ width: 300, maxWidth: '100%' }} required>
                  <InputLabel id="courseName-label" sx={{ color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.primary.main } }}>
                    Course Name
                  </InputLabel>
                  <Select
                    labelId="courseName-label"
                    name="courseName"
                    value={formData.courseName}
                    label="Course Name"
                    onChange={handleChange}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.text.primary,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>Select a course</em>
                    </MenuItem>
                    {staticCourseData.map((course) => (
                      <MenuItem key={course.id} value={course.name}>
                        {course.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ width: 300, maxWidth: '100%' }} required>
                  <InputLabel id="moduleName-label" sx={{ color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.primary.main } }}>
                    Module Name
                  </InputLabel>
                  <Select
                    labelId="moduleName-label"
                    name="moduleName"
                    value={formData.moduleName}
                    label="Module Name"
                    onChange={handleChange}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.text.primary,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>Select a module</em>
                    </MenuItem>
                    {staticModuleData.map((module) => (
                      <MenuItem key={module.id} value={module.name}>
                        {module.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl sx={{ width: 300, maxWidth: '100%' }} required>
                  <InputLabel id="topicName-label" sx={{ color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.primary.main } }}>
                    Topic Name
                  </InputLabel>
                  <Select
                    labelId="topicName-label"
                    name="topicName"
                    value={formData.topicName}
                    label="Topic Name"
                    onChange={handleChange}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.text.primary,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>Select a topic</em>
                    </MenuItem>
                    {staticTopicData.map((topic) => (
                      <MenuItem key={topic.id} value={topic.name}>
                        {topic.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ width: 300, maxWidth: '100%' }} required>
                  <InputLabel id="questionType-label" sx={{ color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.primary.main } }}>
                    Question Type
                  </InputLabel>
                  <Select
                    labelId="questionType-label"
                    name="questionType"
                    value={formData.questionType}
                    label="Question Type"
                    onChange={handleChange}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.text.primary,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>Select a question type</em>
                    </MenuItem>
                    {staticQuestionTypeData.map((type) => (
                      <MenuItem key={type.id} value={type.name}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Question"
                  variant="outlined"
                  fullWidth
                  name="question"
                  value={formData.question}
                  onChange={handleChange}
                  required
                  multiline
                  rows={4}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.text.primary,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    },
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.primary,
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: theme.palette.primary.main,
                    },
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Tooltip
                  title={
                    !formData.courseName
                      ? 'Select Course'
                      : !formData.moduleName
                        ? 'Select Module'
                        : !formData.topicName
                          ? 'Select Topic'
                          : !formData.questionType
                            ? 'Select Question Type'
                            : !formData.question
                              ? 'Enter Question'
                              : ''
                  }
                  arrow
                >
                  <span>
                    <Button
                      variant="contained"
                      onClick={handleUpdate}
                      sx={{
                        mr: 2,
                        backgroundColor: theme.palette.primary.main,
                        '&:hover': { backgroundColor: theme.palette.primary.dark },
                      }}
                      disabled={
                        !formData.courseName ||
                        !formData.moduleName ||
                        !formData.topicName ||
                        !formData.questionType ||
                        !formData.question
                      }
                    >
                      UPDATE
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleCancel}
                >
                  CANCEL
                </Button>
              </Box>
            </Box>
          </Box>
        </CustomTableScrollbar>
      </Modal>

      {/* View Question Modal */}
      <Modal
        open={showViewQuestionModal}
        onClose={handleCancel}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 600,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
            maxHeight: '80vh',
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
              View Question
            </Typography>
            <IconButton onClick={handleCancel} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ maxHeight: 'calc(80vh - 64px)', overflowY: 'auto', padding: '20px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControlLabel
                control={<Checkbox checked={formData.isActive} disabled />}
                label="Is Active"
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Course Name"
                  variant="outlined"
                  fullWidth
                  value={formData.courseName}
                  InputProps={{ readOnly: true }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.primary,
                    },
                  }}
                />
                <TextField
                  label="Module Name"
                  variant="outlined"
                  fullWidth
                  value={formData.moduleName}
                  InputProps={{ readOnly: true }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.primary,
                    },
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Topic Name"
                  variant="outlined"
                  fullWidth
                  value={formData.topicName}
                  InputProps={{ readOnly: true }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.primary,
                    },
                  }}
                />
                <TextField
                  label="Question Type"
                  variant="outlined"
                  fullWidth
                  value={formData.questionType}
                  InputProps={{ readOnly: true }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.primary,
                    },
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Question"
                  variant="outlined"
                  fullWidth
                  value={formData.question}
                  InputProps={{ readOnly: true }}
                  multiline
                  rows={4}
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.primary,
                    },
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleCancel}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    '&:hover': { backgroundColor: theme.palette.primary.dark },
                  }}
                >
                  CLOSE
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={showUserGuideModal}
        onClose={() => setShowUserGuideModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 800,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'relative',
            }}
          >
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Question Management - For User Guide
            </Typography>
            <IconButton
              onClick={() => setShowUserGuideModal(false)}
              sx={{ color: '#fff', position: 'absolute', right: 14 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Scrollable Content */}
         <CustomTableScrollbar sx={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px' }}>
  {/* Section 1: Add Question */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}
  >
    ➤ Follow these steps to add a new question:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>
      Click the <strong>"Add Question"</strong> button at the top-right corner to open the Add Question form.
    </li>
    <li>
      Select a <strong>Module Name</strong> and <strong>Topic Name</strong> from their respective dropdown menus.
    </li>
    <li>
      Choose a <strong>Question Type</strong> (for example, Multiple Choice or Descriptive) from the dropdown list.
    </li>
    <li>
      Enter the <strong>question text</strong> in the provided multiline input field.
    </li>
    <li>
      Enable the <strong>"Is Active"</strong> checkbox if the question should be active.
    </li>
    <li>
      Before clicking the <strong>"SAVE"</strong> button, carefully review all entered details for accuracy.
    </li>
    <li>
      Click the <strong>"SAVE"</strong> button to add the question. A success message will confirm the action.
    </li>
    <li>
      The newly added question will then appear in the <strong>Question List</strong> table.
    </li>
    <li>
      To clear the form, click <strong>"RESET"</strong>, or click <strong>"CANCEL"</strong> to close the modal without saving.
    </li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: Course Name, Module Name, Topic Name, Question Type, and Question are mandatory fields.
  </Typography>

  {/* Section 2: Edit Question */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to Update an existing question:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>
      Select the question record you want to edit from the <strong>Question List</strong> table.
    </li>
    <li>
      Click the <strong>Edit</strong> icon (pencil) in the Action column to open the edit question form.
    </li>
    <li>
      In the edit modal, update the Course Name, Module Name, Topic Name, Question Type, Question text, or Is Active status as required.
    </li>
    <li>
      Before clicking the <strong>"UPDATE"</strong> button, ensure all required fields are properly filled.
    </li>
    <li>
      Click the <strong>"UPDATE"</strong> button to save the changes. A success message will confirm the update.
    </li>
    <li>
      To clear the form, click <strong>"RESET"</strong>, or click <strong>"CANCEL"</strong> to close the modal without saving changes.
    </li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: Course Name, Module Name, Topic Name, Question Type, and Question are mandatory fields.
  </Typography>

  {/* Section 3: View Question */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to view a question:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>
      Select the question record you want to view from the <strong>Question List</strong> table.
    </li>
    <li>
      Click the <strong>View</strong> icon (eye) in the Action column to open the view question modal.
    </li>
    <li>
      Review the question details, including Course Name, Module Name, Topic Name, Question Type, Question text, and Is Active status.
    </li>
    <li>
      Click the <strong>"CLOSE"</strong> button to exit the modal.
    </li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: The view modal displays question details in read-only mode.
  </Typography>

  {/* Section 4: Delete Question */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to delete a question:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>
      Select the question record you want to delete from the <strong>Question List</strong> table.
    </li>
    <li>
      Click the <strong>Delete</strong> icon (trash) in the Action column.
    </li>
    <li>
      Confirm the deletion by clicking <strong>"Yes, Delete"</strong> in the confirmation modal.
    </li>
    <li>
      The question will be removed from the list, and a success message will appear at the top of the page.
    </li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: Deleted questions cannot be recovered. Please proceed carefully.
  </Typography>

  {/* Section 5: Import Questions */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to import questions:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>
      Click the <strong>"Import"</strong> button at the top-right corner to open the import modal.
    </li>
    <li>
      Click <strong>"Select File"</strong> to choose a file containing question data.
    </li>
    <li>
      Verify that the selected file name is displayed correctly.
    </li>
    <li>
      Click the <strong>"IMPORT"</strong> button to begin importing questions. A success message will confirm completion.
    </li>
    <li>
      To cancel the import, click <strong>"RESET"</strong> to clear the selected file or <strong>"CANCEL"</strong> to close the modal.
    </li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: A valid file must be selected to enable the import action.
  </Typography>

  {/* Section 6: Search Questions */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to search for a question:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>
      Use the search bar located at the top-left of the Question List table.
    </li>
    <li>
      Enter the Question text, Course, Module, Topic, or Question Type (or part of it) to filter the results.
    </li>
    <li>
      The table will automatically update to display matching records.
    </li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: The search is case-insensitive and filters across all question fields.
  </Typography>

  {/* Section 7: Pagination */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to navigate the question list:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>
      Use the pagination controls at the bottom of the table to navigate through the question list.
    </li>
    <li>
      Click the left (<strong>&lt;</strong>) or right (<strong>&gt;</strong>) arrows to move between pages.
    </li>
    <li>
      Click on a specific page number to jump directly to that page.
    </li>
    <li>
      Change the number of rows displayed per page using the dropdown (for example, 5, 10, 25, or 50 rows).
    </li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: The table displays only the selected number of rows per page.
  </Typography>
</CustomTableScrollbar>

        </Box>
      </Modal>
    </Container>
  );
};

export default Question;